/* @format */

import { api } from '@/scripts/api';
import { PromoService } from '@/scripts/services/entities/promo-service';
import { DEFAULT_PROMO_ID } from '@/scripts/promos';

export const ClientService = {
    /**
     * Get clients with the provided OData query options.
     *
     * @param {{
     *      query?: ODataQueryOptions;
     *      signal?: AbortSignal;
     * }} options
     */
    async getClients(options) {
        const queryParams = api.buildODataQuery(options.query ?? {});
        const endpoint = `/service/Clients${queryParams}`;
        return api.ajax({
            method: 'GET',
            url: endpoint,
            signal: options.signal,
        });
    },

    /**
     * Get a client by its ID.
     *
     * @param {{
     *      clientId: number;
     *      query?: ODataQueryOptions;
     *      signal?: AbortSignal;
     * }} options
     */
    async getClientById(options) {
        return this.getClients({
            query: {
                ...(options.query ?? {}),
                filter: {
                    ...(options.query?.filter ?? {}),
                    Id: options.clientId,
                },
            },
            signal: options.signal,
        });
    },

    async isClientLegacyAccess(clientId) {
        const freePromosRes = await PromoService.getPromos({
            query: {
                select: ['Id'],
                filter: {
                    not: {
                        Id: DEFAULT_PROMO_ID,
                    },
                },
            },
        });

        const freePromos = freePromosRes?.data?.value ?? [];
        const freePromoIds = freePromos.map((promo) => promo.Id);

        const freePromoClientRes = await this.getClients({
            query: {
                select: ['Id'],
                filter: {
                    Id: clientId,
                    IsActive: true,
                    or: [
                        {
                            ManagesSubscription: false,
                        },
                        ...freePromoIds.map((promoId) => ({
                            PromoId: promoId,
                        })),
                    ],
                },
            },
        });

        const isFreePromoClient = freePromoClientRes?.data?.value?.length > 0 ?? false;
        return isFreePromoClient;
    },

    /**
     * Activate a "free loader's" subscription.
     *
     * @param clientId {number}
     */
    async activateSubscription(clientId) {
        return api.post(`/api/Clients/${clientId}/ActivateSubscription`);
    },

    /**
     * Get the client's next billing date.
     *
     * @param clientId {number}
     */
    async getNextBillingDate(clientId) {
        return api.get(`/api/Clients/${clientId}/Account/NextBillingDate`);
    },

    /**
     * Get the client's next billing amount.
     *
     * @param clientId {number}
     */
    async getNextBillingAmount(clientId) {
        return api.get(`/api/Clients/${clientId}/Account/NextBillingAmount`);
    },

    /**
     * Get the (free-loader) client's next billing amount for activating a subscription.
     *
     * @param clientId
     */
    async getReactivationNextBillingAmount(clientId) {
        return api.get(`/api/Clients/${clientId}/Account/Reactivation/NextBillingAmount`);
    },
};
